import { DeviceType, SettingMode } from '@/common/constants';
import Axios, { ZoneApi, FirmwareApi, ProductApi, NoticeApi, CtrlApi, VirtualDeviceApi } from '@/api';
import { MQTTCommand, MQTTTempCommand, MQTTCurtainCommand, parseBoilerRelayOn } from '../common/constants';
import { resolveControllerOtaMeta } from '@/common/utils/controllerOta';
import { toDisplayLampPort } from '@/common/utils/controllerDisplayName';
import {
  isPhysicalMappingHostDevice,
  isPhysicalMappingHostOrigin,
  memberShowsHostState,
  onPhysicalMappingHostControl,
  patchMappingMemberOnVirtual,
  resolveMappingHostId,
  shouldSkipMemberHostEcho,
} from '@/common/utils/physicalMappingMember';
import sceneRelease from '@/common/utils/sceneRelease';

/** 같은 가상버튼에 설정모드 이벤트가 겹쳐 들어와도 할당 요청은 한 번만 */
const boundButtonAssignInflight = new Set();
/** 이번 대기에서 성공 알림을 이미 올렸으면 중복 응답은 팝업을 다시 띄우지 않는다 */
const boundButtonAssignNotified = new Set();

export default {
  namespaced: true,
  state: {
    current: {
      zoneId: 0,
      groupId: 0,
      deviceId: 0,
    },
    settingModeDevices: [],
    zones: [],
    firmwares: [],
    products: [],
    notices: [],
    checkControllers: {},
    isWebViewReady: false,
    upgradeTempController: undefined,
    isPublishSchedules: false,
    isSyncControllerDevices: false,
    virtualSwitchSettingDevice: undefined,
    virtualSwtichSettingCount: 0,
    /** 가상버튼↔물리 벽버튼 할당 대기 중인 가상버튼 Device */
    boundButtonSettingDevice: undefined,
    boundButtonSettingCount: 0,
    boundButtonLastError: undefined,
    /** 최근 할당 시 흡수 결과 (Phase2) */
    boundButtonLastAbsorb: undefined,
    /** 가상버튼→멤버 발행 직후, 같은 전원 보고로 가상버튼 UI가 꺼지는 것 방지 */
    virtualButtonOffSuppressUntil: 0,
    virtualButtonOffSuppressOnoff: null,
    dooya: { 
      isOn: false, 
      settingDevice: undefined,
      addressStatus: "idle",
      directionStatus: 0,
    },
    rono: { 
      isOn: false, 
      settingDevice: undefined,
      addressStatus: "idle",
      directionStatus: 0,
    },
  },
  getters: {
    getZones: (state) => {
      return state.zones;
    },
    getCurrent: (state) => {
      return state.current;
    },
    getCurrentZone: (state) => {
      return state.zones.find((z) => z._id === state.current.zoneId);
    },
    getCurrentGroup: (state) => {
      return state.zones
        ?.find((z) => z._id === state.current.zoneId)
        ?.groups.find((g) => g._id === state.current.groupId);
    },
    getCurrentDevice: (state) => {
      return state.zones
        ?.find((z) => z._id === state.current.zoneId)
        ?.groups.find((g) => g._id === state.current.groupId)
        ?.devices.find((d) => d._id === state.current.deviceId);
    },

    getSettingModeDevices: (state) => {
      return state.settingModeDevices;
    },
    getFirmwares: (state) => {
      return state.firmwares;
    },
    getProducts: (state) => {
      return state.products;
    },
    getNotices: (state) => {
      return state.notices;
    },
    getControllers: (state, getters, rootState) => {
      if (state.zones?.length === 0) {
        return [];
      }

      const filteredControllers = [];
      state.zones.filter(zone => zone.ownerId == rootState.auth?.account?._id).forEach((zone) => {
        const controllers = zone.controllers
          ?.map((controller) => {
            const ota = resolveControllerOtaMeta(controller, state.products, state.firmwares);
            return {
              ...controller,
              ...(ota.version != null ? { version: ota.version } : {}),
              lastVersion: ota.lastVersion,
              downloadUrl: ota.downloadUrl,
              useLegacyUpgrade1: ota.useLegacyUpgrade1,
            };
          }) || [];
          
          filteredControllers.push(...controllers);
      });

      return [...new Set(filteredControllers)];
    },
    getCurrentZoneControllers: (state, getters, rootState) => {
      if (!state.zones?.length || !state.current?.zoneId) {
        return [];
      }
      const zone = state.zones.find((z) => z._id === state.current.zoneId);
      if (!zone?.controllers?.length) {
        return [];
      }
      return zone.controllers.map((controller) => {
        const ota = resolveControllerOtaMeta(controller, state.products, state.firmwares);
        return {
          ...controller,
          ...(ota.version != null ? { version: ota.version } : {}),
          lastVersion: ota.lastVersion,
          downloadUrl: ota.downloadUrl,
          useLegacyUpgrade1: ota.useLegacyUpgrade1,
        };
      });
    },
    getLowVersionControllers: (state, getters, rootState) => {
      if (state.zones?.length === 0 || state.products?.length === 0) {
        return [];
      }

      const filteredControllers = [];
      state.zones.filter(zone => zone.ownerId == rootState.auth?.account?._id).forEach((zone) => {
        const controllers = zone.controllers
          ?.map((controller) => {
            const ota = resolveControllerOtaMeta(controller, state.products, state.firmwares);
            return {
              ...controller,
              ...(ota.version != null ? { version: ota.version } : {}),
              lastVersion: ota.lastVersion,
              downloadUrl: ota.downloadUrl,
              useLegacyUpgrade1: ota.useLegacyUpgrade1,
            };
          })
          ?.filter((controller) => controller.lastVersion && controller.lastVersion > controller.version) || [];
          
          filteredControllers.push(...controllers);
      });

      return [...new Set(filteredControllers)];
    },
    getFirmwareUpdatingControllers: (state, getters, rootState) => {
      const updateControllers = [];
      state.zones.filter(zone => zone.ownerId == rootState.auth?.account?._id).forEach((z) => {          
        updateControllers.push(...z.controllers.filter((ctrl) => ctrl.firmwareState === "fs") || []);
      })

      return updateControllers

      // return (
      //   state.zones
      //     .find((zone) =>
      //       zone.controllers.some((ctrl) => ctrl.firmwareState === 'fs'),
      //     )
      //     ?.controllers.filter((ctrl) => ctrl.firmwareState === 'fs') || []
        
      // );
    },
    getFirmwareUpdatedControllers: (state, getters, rootState) => {
      const updateControllers = [];
      state.zones.filter(zone => zone.ownerId == rootState.auth?.account?._id).forEach((z) => {          
        updateControllers.push(...z.controllers.filter((ctrl) => ctrl.firmwareState === "fe") || []);
      });

      return updateControllers;

      // return (
      //   state.zones
      //     .find((zone) =>
      //       zone.controllers.some((ctrl) => ctrl.firmwareState === 'fe'),
      //     )
      //     ?.controllers.filter((ctrl) => ctrl.firmwareState === 'fe') || []        
      // );
    },
    availableFirmwareUpdate : (state, getters, rootState) => {
      return state.zones.filter(zone => zone.ownerId == rootState.auth?.account?._id).filter((zone) =>
        zone.controllers?.some((controller) => {
          let firmware = state.firmwares.find(
            (firmware) => controller.model === firmware.model
          );
          return controller.version !== firmware.version;
        })
      );
    },
    isWebViewReady: (state) => {
      return state.isWebViewReady;
    },
    getCheckControllers: (state) => {
      return state.checkControllers;
    },
    getUpgradeTempController: (state) => {
      return state.upgradeTempController;
    },
    getIsPublishSchedules: (state) => {
      return state.isPublishSchedules;
    },
    getIsSyncControllerDevices: (state) => {
      return state.isSyncControllerDevices;
    },
    getVirtualSwitchSettingDevice: (state) => {
      return state.virtualSwitchSettingDevice;
    },
    getVirtualSwtichSettingCount: (state) => {
      return state.virtualSwtichSettingCount;
    },
    getBoundButtonSettingDevice: (state) => {
      return state.boundButtonSettingDevice;
    },
    getBoundButtonSettingCount: (state) => {
      return state.boundButtonSettingCount;
    },
  },
  mutations: {
    setVirtualSwitchSettingDevice: (state, payload) => {
      state.virtualSwitchSettingDevice = payload;
    },
    increaseVirtualSwtichSettingCount: (state) => {
      state.virtualSwtichSettingCount = (state.virtualSwtichSettingCount || 0) + 1;
    },
    setBoundButtonSettingDevice: (state, payload) => {
      state.boundButtonSettingDevice = payload;
      if (payload) {
        state.boundButtonLastError = undefined;
        state.boundButtonLastAbsorb = undefined;
      }
    },
    increaseBoundButtonSettingCount: (state) => {
      state.boundButtonSettingCount = (state.boundButtonSettingCount || 0) + 1;
    },
    setBoundButtonLastError: (state, payload) => {
      state.boundButtonLastError = payload;
    },
    setBoundButtonLastAbsorb: (state, payload) => {
      state.boundButtonLastAbsorb = payload;
    },
    updateDeviceBoundButton: (state, { deviceId, boundButton }) => {
      state.zones = state.zones.map((zone) => ({
        ...zone,
        groups: (zone.groups || []).map((group) => ({
          ...group,
          devices: (group.devices || []).map((device) =>
            device._id == deviceId ? { ...device, boundButton } : device,
          ),
        })),
      }));
    },
    suppressVirtualButtonOff: (state, payload) => {
      const ms = payload != null && typeof payload === 'object' ? payload.ms : payload;
      const duration = Number(ms) > 0 ? Number(ms) : 3000;
      const until = Date.now() + duration;
      state.virtualButtonOffSuppressUntil = Math.max(
        state.virtualButtonOffSuppressUntil || 0,
        until,
      );
      if (payload != null && typeof payload === 'object' && payload.onoff != null) {
        state.virtualButtonOffSuppressOnoff = Number(payload.onoff) === 1 ? 1 : 0;
      } else {
        state.virtualButtonOffSuppressOnoff = null;
      }
    },
    setCurrentZone: (state, id) => {
      state.current.zoneId = id;
    },
    setCurrentGroup: (state, id) => {
      state.current.groupId = id;
    },
    setCurrentDevice: (state, id) => {
      state.current.deviceId = id;
    },
    setCurrent: (state, payload) => {
      state.current = payload;
    },
    setZones: (state, payload) => {
      state.zones = payload.map(zone => ({
        ...zone,
        groups: zone.groups.sort((a, b) => {
          if (a.sort && !b.sort) return -1;
          if (!a.sort && b.sort) return 1;
          if (!a.sort && !b.sort) return 0;
          if (a.sort > b.sort) return 1;
          if (a.sort < b.sort) return -1;
          return 0;
        }).map((group) => ({
          ...group,
          devices: (group.devices || []).map((device) => {
            if (device.type !== 2 || device.isVirtual || !device.item) {
              return device;
            }
            if (device.item.onoff === undefined || device.item.onoff === null) {
              return {
                ...device,
                item: { ...device.item, onoff: 1 },
              };
            }
            return device;
          }),
        })),
      }));
    },
    setDeviceState: (state, payload) => {
      console.log('store/home => setDeviceState:',payload)

      let device = state.zones
        .find((z) =>
          z.groups.some((g) =>
            g.devices.some((d) => d._id === payload.deviceId),
          ),
        )
        .groups.find((g) => g.devices.some((d) => d._id === payload.deviceId))
        .devices.find((d) => d._id === payload.deviceId);

      // console.log('store/home => setDeviceState => device : ',device)

      // 전원은 모든 디바이스 동일
      // device.item.onoff = payload.state.onoff;

      if (payload.state.onVirtual != undefined) device.onVirtual = payload.state.onVirtual;

      switch (payload.type) {
        case 1: //조명
          device.item.onoff = payload.state.onoff;
          if (payload.state.bright == 0 && payload.state.color == 0) {
            console.log("payload.state.bright:", payload.state.bright, ", payload.state.color", payload.state.color)
          } else {
            if (payload.state.bright) device.item.bright = payload.state.bright;
            if (payload.state.color !== undefined) device.item.color = payload.state.color;
          }
          break;
        case 2: //보일러
          device.item = {
            ...device.item,
            ...payload.state,
            customPattern: {
              ...device.item.customPattern,
              ...(payload.state.customPattern || {}) 
            },
            aircon: {
              ...(device.item.aircon || {}),
              ...(payload.state.aircon || {}),
            },
          };
          break;
        case 3: //커튼
        case 5:
          console.log({
            ...device.item,
            ...payload.state
          })
          device.item = {
            ...device.item,
            ...payload.state
          };
          break;
        case 4: //뢈춘디
          device.item.onoff = payload.state.onoff;
          break;
      }
    },
    setVirtualDeviceState: (state, payload) => {
      console.log('store/home => setVirtualDeviceState:', payload);
      let device = state.zones
        .find((z) =>
          z.groups.some((g) =>
            g.devices.some((d) => d._id === payload.deviceId),
          ),
        )
        .groups.find((g) => g.devices.some((d) => d._id === payload.deviceId))
        .devices.find((d) => d._id === payload.deviceId);

      if (!device || !device.item) {
        return;
      }

      if (!device.item.state) {
        device.item.state = {};
      }

      switch (payload.type) {
        case 1: //조명
          // vc: onoff만 반영. bright/color는 임시 오버레이이므로
          // 대표버튼 영구 look(item.bright/color)을 덮어쓰지 않는다.
          // 대표버튼 UI는 onVirtual 시 item.state 를 쓰므로 덮어쓰지 않는다.
          if (payload.state.onoff !== undefined) {
            device.item.onoff = payload.state.onoff;
          }
          // API onVirtual 반영보다 vc echo가 먼저 오면 차일드가 잠깐 켜져 보임 → 즉시 대표 UI OFF
          if (!device.isVirtual && Number(payload.state?.onoff) === 1) {
            device.onVirtual = true;
            device.item.state.onoff = 0;
          }
          break;
        case 2: //보일러
          device.item.state = {
            ...device.item.state,
            ...payload.state,
          };
          break;
        case 3: //커튼
        case 5:
          device.item.state = {
            ...device.item.state,
            ...payload.state
          };
          break;
        case 4: //뢈춘디
          if (payload.state.onoff !== undefined) {
            device.item.onoff = payload.state.onoff;
          }
          break;
      }
    },
    setDevicesState: (state, payload) => {
      console.log('store/home => setDevicesState:', payload);
      const deviceIds = payload.deviceIds ?? [];
      const devices = deviceIds.map((id) => {
        const zone = state.zones.find((z) =>
          z.groups.some((g) => g.devices.some((d) => d._id === id))
        );
        if (!zone) return null;
        const group = zone.groups.find((g) => g.devices.some((d) => d._id === id));
        if (!group) return null;
        return group.devices.find((d) => d._id === id) ?? null;
      }).filter(Boolean);

      devices.forEach((device) => {
        switch (device.type) {
          case DeviceType.Lamp: {
            const state = payload.state ?? {};
            console.log('device: ', device, 'state: ', state);
            const toScalar = (v) => (Array.isArray(v) ? v[0] : v);
            if (state.onoff !== undefined) device.item.onoff = toScalar(state.onoff);
            if (state.bright !== undefined) device.item.bright = toScalar(state.bright);
            if (state.color !== undefined) device.item.color = toScalar(state.color);
            break;
          }
          case DeviceType.Boiler:
            break;
          case DeviceType.Curtain:
          case DeviceType.Dooya:
          case DeviceType.Rono:
            break;
          case DeviceType.Ventilator:
            break;
        }
      });
    },
    setVirtualDevicesState: (state, payload) => {
      console.log('store/home => setVirtualDevicesState:', payload);
      const deviceIds = payload.deviceIds ?? [];
      const devices = deviceIds.map((id) => {
        const zone = state.zones.find((z) =>
          z.groups.some((g) => g.devices.some((d) => d._id === id))
        );
        if (!zone) return null;
        const group = zone.groups.find((g) => g.devices.some((d) => d._id === id));
        if (!group) return null;
        return group.devices.find((d) => d._id === id) ?? null;
      }).filter(Boolean);

      devices.forEach((device) => {
        switch (device.type) {
          case DeviceType.Lamp: {
            const state = payload.state ?? {};
            console.log('device: ', device, 'state: ', state);
            // vc 배치: onoff만 반영 (대표버튼 look item.bright/color·UI item.state 유지)
            if (state.onoff !== undefined) device.item.onoff = state.onoff;
            if (!device.isVirtual && Number(state.onoff) === 1) {
              device.onVirtual = true;
              if (!device.item.state) device.item.state = {};
              device.item.state.onoff = 0;
            }
            break;
          }
          case DeviceType.Boiler:
            break;
          case DeviceType.Curtain:
          case DeviceType.Dooya:
          case DeviceType.Rono:
            break;
          case DeviceType.Ventilator:
            break;
        }
      });
    },
    addZone: (state, payload) => {
      console.log(payload);
      state.zones = state.zones.concat(payload);
      if (state.zones.length === 1) {
        state.current.zoneId = payload._id;
      }
    },
    addGroup: (state, payload) => {
      const targetZone = state.zones.find((z) => z._id == payload?.zoneId);
      if (!targetZone) {
        return;
      }

      state.zones = state.zones.map((zone) =>
        zone._id == payload.zoneId
          ? {
              ...zone,
              groups: zone.groups.concat(payload.data).sort((a, b) => {
                if (a.sort && !b.sort) return -1;
                if (!a.sort && b.sort) return 1;
                if (!a.sort && !b.sort) return 0;
                if (a.sort > b.sort) return 1;
                if (a.sort < b.sort) return -1;
                return 0;
              }),
            }
          : zone,
      );
    },
    addDevice: (state, payload) => {
      const targetZone = state.zones.find((z) => z._id == payload?.zoneId);
      const targetGroup = targetZone?.groups?.find(
        (group) => group._id == payload?.groupId,
      );
      if (!targetZone || !targetGroup) {
        return;
      }

      console.log(payload);

      let group = state.zones
        .find((z) => z._id === payload?.zoneId)
        .groups.find((g) => g._id === payload?.groupId);
      if (group) group.devices.push(payload.data);

      // state.zones = state.zones.map((zone) =>
      //   zone._id == payload?.zoneId
      //     ? {
      //         ...zone,
      //         groups: zone.groups.map((group) =>
      //           group._id == payload?.groupId
      //             ? {
      //                 ...group,
      //                 devices: group.devices.concat(payload.data),
      //               }
      //             : group,
      //         ),
      //       }
      //     : zone,
      // );
    },
    updateZone: (state, payload) => {
      const currentZone = state.zones.find(
        (z) => z._id === state.current.zoneId,
      );
      if (!currentZone) {
        return;
      }
      
      state.zones = state.zones.map((zone) =>
        zone._id === state.current.zoneId
          ? {
              ...zone,
              ...payload.data,
            }
          : zone,
      );
    },
    updateGroup: (state, payload) => {
      const currentZone = state.zones.find(
        (z) => z._id === state.current.zoneId,
      );
      let targetGroup = currentZone?.groups?.find(
        (group) => group._id === payload?.id,
      );
      if (!currentZone || !targetGroup) {
        return;
      }

      state.zones = state.zones.map((zone) =>
        zone._id === state.current.zoneId
          ? {
              ...zone,
              groups: zone.groups.map((group) =>
                group._id === payload?.id
                  ? {
                      ...group,
                      ...payload.data,
                    }
                  : group,
              ).sort((a, b) => {
                if (a.sort && !b.sort) return -1;
                if (!a.sort && b.sort) return 1;
                if (!a.sort && !b.sort) return 0;
                if (a.sort > b.sort) return 1;
                if (a.sort < b.sort) return -1;
                return 0;
              }),
            }
          : zone,
      );
    },
    patchDeviceById: (state, { deviceId, data }) => {
      state.zones = state.zones.map((zone) => ({
        ...zone,
        groups: zone.groups.map((group) => ({
          ...group,
          devices: group.devices.map((device) => {
            if (String(device._id) !== String(deviceId)) return device;
            return {
              ...device,
              ...data,
              item: data?.item
                ? { ...device.item, ...data.item }
                : device.item,
            };
          }),
        })),
      }));
    },
    setVirtualButtonOffIfLinked: (state, { virtualDocs, reportedMemberId }) => {
      state.zones = state.zones.map((zone) => ({
        ...zone,
        groups: zone.groups.map((group) => ({
          ...group,
          devices: sceneRelease.applyMemberRelease(group.devices, virtualDocs, reportedMemberId),
        })),
      }));
    },
    updateDevice: (state, payload) => {
      console.log('store/home => updateDevice:', payload);
      const currentZone = state.zones.find(
        (z) => z._id == state.current.zoneId,
      );
      let targetGroup = currentZone?.groups?.find(
        (group) => group._id == payload?.groupId,
      );
      if (!currentZone || !targetGroup) {
        return;
      }

      state.zones = state.zones.map((zone) =>
        zone._id == currentZone._id
          ? {
              ...zone,
              groups: zone.groups.map((group) =>
                group._id == targetGroup._id
                  ? {
                      ...group,
                      devices: group.devices.map((device) =>
                        device._id == payload?.id
                          ? {
                              ...device,
                              ...payload.data,
                              item: payload.data?.item ? {
                                ...device.item,
                                ...payload.data.item
                              } : device.item
                            }
                          : device,
                      ),
                    }
                  : group,
              ),
            }
          : zone,
      );
    },
    updateDeviceVirtualMap: (state, { deviceId, virtualMap }) => {
      state.zones = state.zones.map((zone) => ({
        ...zone,
        groups: (zone.groups || []).map((group) => ({
          ...group,
          devices: (group.devices || []).map((device) =>
            device._id == deviceId ? { ...device, virtualMap } : device,
          ),
        })),
      }));
    },
    deleteZone: (state, payload) => {
      if (state.current.zoneId === payload) {
        state.current.zoneId = 0;
      }

      state.zones = state.zones.filter((item) => item._id !== payload);

      if (state.zones?.length > 0) {
        state.current.zoneId = state.zones[0]._id;
      }
    },
    deleteController:  (state, payload) => {
      state.zones = state.zones.map((zone) =>
        zone._id == payload.zoneId
          ? {
              ...zone,
              controllers: zone.controllers?.filter(
                (controller) => controller._id != payload.controllerId,
              ),
            }
          : zone,
      );
    },
    deleteGroup: (state, payload) => {
      state.zones = state.zones.map((zone) =>
        zone._id == payload.zoneId
          ? {
              ...zone,
              groups: zone.groups.filter(
                (group) => group._id != payload.groupId,
              ),
            }
          : zone,
      );
    },
    deleteDevice: (state, payload) => {
      let group = state.zones
        .find((z) => z._id == payload?.zoneId)
        .groups?.find((g) => g._id == payload?.groupId);
      let idx = group?.devices?.findIndex((gd) => gd._id == payload?.deviceId);
      if (idx >= 0) group.devices.splice(idx, 1);

      // state.zones = state.zones.map((zone) =>
      //   zone._id == payload.zoneId
      //     ? {
      //         ...zone,
      //         groups: zone.groups.map((group) =>
      //           group._id == payload.groupId
      //             ? {
      //                 ...group,
      //                 devices: group.devices.filter(
      //                   (device) => device._id != payload.deviceId,
      //                 ),
      //               }
      //             : group,
      //         ),
      //       }
      //     : zone,
      // );
    },

    changeDeviceSettingMode: (state, payload) => {
      let idx = state.settingModeDevices.findIndex(
        (d) => {
          if (d?.serial == payload?.serial &&
          d.type == payload.type &&
          ((d.type == DeviceType.Lamp && d.data?.port == payload.data.port && d.data?.port !== undefined && payload.data.port !== undefined) ||
            // d.data?.ports.every(
            //   (val, idx) => val === payload.data?.ports[idx],
            // ))
            (d.type > DeviceType.Lamp && d.data?.serial == payload.data?.serial && d.data?.serial !== undefined && payload.data?.serial !== undefined)))
            return d;
        }
      );

      if (payload.data.setting == SettingMode.InSettingMode) {
        if (idx < 0) {
          state.settingModeDevices.push(payload);
        }
      } else {
        if (idx >= 0) state.settingModeDevices.splice(idx, 1);
      }
    },
    removeSettingModeDevice: (state, payload) => {
      let idx = state.settingModeDevices.findIndex(
        (d) =>
          d?.serial == payload?.serial &&
          d.type == payload.type &&
          ((d.type == DeviceType.Lamp &&
            // d.data?.ports.every(
            //   (val, idx) => val === payload.data?.ports[idx],
            // ))
            d.data?.port == payload.data.port) ||
            (d.type > DeviceType.Lamp &&
              d.data?.serial == payload.data?.serial)),
      );

      if (idx >= 0) state.settingModeDevices.splice(idx, 1);
    },
    removeAllSettingModeDevices: (state) => {
      state.settingModeDevices = [];
    },

    changeControllerFirmwareUpdateState: (state, payload) => {
      let controller = state.zones
        .find((z) => z.controllers.some((zc) => zc._id === payload?.serial))
        .controllers.find((zc) => zc._id === payload?.serial);
      controller.firmwareState = payload?.cmd;
    },

    updateControllerFirmwareVersion: (state, payload) => {
      let controller = state.zones
        .find((z) => z.controllers.some((zc) => zc._id === payload?.serial))
        .controllers.find((zc) => zc._id === payload?.serial);
      if (controller.firmwareState === MQTTCommand.FirmwareUpdateStarted && controller.version !== payload.version) {
        controller.firmwareState = MQTTCommand.FirmwareUpdateFinished;
      }
      controller.version = payload.version;
    },

    updateController: (state, payload) => {
      const controllerData = Array.isArray(payload) ? payload[0] : payload;
      if (!controllerData?._id) return;
      const zone = state.zones.find((z) => z.controllers?.some((zc) => zc._id === controllerData._id));
      if (!zone) return;
      const controller = zone.controllers.find((zc) => zc._id === controllerData._id);
      if (!controller) return;
      Object.assign(controller, controllerData);
    },

    setFirmwares: (state, payload) => {
      state.firmwares = payload;
    },

    setProducts: (state, payload) => {
      state.products = payload;
    },

    setNotices: (state, payload) => {
      state.notices = payload;
    },

    setZoneEditMode: (state, payload) => {
      console.log(
        `home/mutations.setZoneEditMode => payload:${JSON.stringify(payload)}`,
      );

      const zone = state.zones.find((z) => z._id === payload.zoneId);
      if (zone) {
        zone.isEditMode = payload.isEditMode;

        // console.log(`home/mutations.setZoneEditMode => zone._id:${zone._id} , currentZoneId : ${state.current.zoneId}`)

        // if(state.current.zoneId === payload.zoneId){

        //   let idx = state.zones.findIndex((zz) => {
        //     return zz.isEditMode === false;
        //   });
        //   idx = idx < 0 ? 0 : idx;
        //   console.log('home/mutations.setZoneEditMode => ',idx)
        //   state.current.zoneId = state.zones[idx]._id
        // }
      }
    },

    initState: (state) => {
      state.current = {
        zoneId: 0,
        groupId: 0,
        deviceId: 0,
      };
      state.settingModeDevices = [];
      state.zones = [];
      state.firmwares = [];
      state.products = [];
    },
    
    setWebViewReady: (state, payload) => {
      state.isWebViewReady = payload;
    },

    updateCheckControllers: (state, payload) => {
      // 0=checking/unknown, 1=online, 3=offline — falsy 0 을 유지하려면 ?? 사용
      state.checkControllers[payload.serial] = payload.value ?? 0;
    },

    setUpgradeTempController: (state, payload) => {
      console.log('home/setUpgradeTempController => payload : ', payload);

      if (!payload?.command || payload?.command != "UPGRADE") {
        return;
      }

      if (payload.value?.Upgrade?.indexOf("Version") > -1) {
        state.upgradeTempController = payload;
        console.log(payload.value?.Upgrade);
      } else {
        state.upgradeTempController = payload;
        if (payload.value?.Upgrade?.indexOf("Successful") > -1) {
          console.log(payload.value?.Upgrade);
        } else {
          console.error(payload.value?.Upgrade);
        }
      }
    },

    setIsPublishSchedules: (state, payload) => {
      state.isPublishSchedules = payload;
    },
    setIsSyncControllerDevices: (state, payload) => {
      state.isSyncControllerDevices = payload;
    },
    setOnDooya: (state, payload) => {
      state.dooya.isOn = payload;
    },
    setDooyaDirectionStatus: (state, payload) => {
      console.log('home/setDooyaDirectionStatus => payload : ', payload);
      state.dooya.directionStatus = payload;
    },
    setDooyaAddressStatus: (state, payload) => {
      state.dooya.addressStatus = payload;
    },
    setDooyaSettingDevice: (state, payload) => {
      state.dooya.settingDevice = payload;
    },
    clearDooya: (state) => {
      state.dooya = { isOn: false, settingDevice: undefined, addressStatus: "idle", directionStatus: 0 };
    },
    setOnRono: (state, payload) => {
      state.rono.isOn = payload;
    },
    setRonoDirectionStatus: (state, payload) => {
      console.log('home/setRonoDirectionStatus => payload : ', payload);
      state.rono.directionStatus = payload;
    },
    setRonoAddressStatus: (state, payload) => {
      state.rono.addressStatus = payload;
    },
    setRonoSettingDevice: (state, payload) => {
      state.rono.settingDevice = payload;
    },
    clearRono: (state) => {
      state.rono = { isOn: false, settingDevice: undefined, addressStatus: "idle", directionStatus: 0 };
    },
  },
  actions: {
    fetchAllZones: async ({ state, commit, rootState }, payload) => {
      return new Promise(async (resolve, reject) => {
        const selectCurrentZone = async (resultData) => {
          const sortedZones = resultData.slice().sort((a, b) => {
            if (a.isOwner && !b.isOwner) return -1; // 이름 기준 오름차순
            if (!a.isOwner && b.isOwner) return 1;
            return 0; // 동일한 경우
          });

          console.log('sortedZones : ',sortedZones)

          commit('setZones', sortedZones);
          let idx = sortedZones.findIndex((zz) => {
            return zz.isEditMode === false;
          });
          idx = idx < 0 ? 0 : idx;

          const account = rootState.auth.account;
          const selectedZoneId = account?.selectedZone;

          if (selectedZoneId && !state.current.zoneId && sortedZones.find(zone => zone._id == selectedZoneId)) {
            commit('setCurrentZone', selectedZoneId);
          } else if (!state.current.zoneId || !sortedZones.find(zone => zone._id == state.current.zoneId)) {
            commit('setCurrentZone', sortedZones[idx]._id);
          }
        };

        Axios.get(ZoneApi.ZONES, {}, '', { hideLoading: payload?.hideLoading })
          .then(async (res) => {
            if (res.resultCode == '0000') {
              console.log(res.resultData)
              if (res.resultData && res.resultData.length > 0) {
                console.log(JSON.stringify(res.resultData))
                selectCurrentZone(res.resultData);
              } else {
                commit('initState');
              }
              return resolve(res.resultData);
            }
            return reject(-1);
          })
          .catch((err) => {
            console.error(err);
            reject(err);
          });
      });
    },
    fetchFirmwares: async (store, payload) => {
      try {
        const result = await Axios.get(FirmwareApi.FIRMWARES, {}, '', { hideLoading: payload?.hideLoading });
        if (result.resultCode === '0000') {
          // console.log(JSON.stringify(result.resultData))
          store.commit('setFirmwares', result.resultData);
        } else {
          console.warn(result.resultMessage);
        }
      } catch (error) {
        console.log(error);
      }
    },
    fetchProducts: async (store, payload) => {
      try {
        const result = await Axios.get(ProductApi.PRODUCTS, {}, '', { hideLoading: payload?.hideLoading });
        if (result.resultCode === '0000') {
          // console.log(JSON.stringify(result.resultData))
          store.commit('setProducts', result.resultData);
        } else {
          console.warn(result.resultMessage);
        }
      } catch (error) {
        console.log(error);
      }
    },
    fetchNotices: async (store, payload) => {
      try {
        const result = await Axios.get(NoticeApi.NOTICES, {}, '', { hideLoading: payload?.hideLoading });
        if (result.resultCode === '0000') {
          // console.log(result.resultData)
          const sortedResultData = result.resultData.sort((a, b) => a._id > b._id ? -1 : (a._id < b._id ? 1 : 0));
          store.commit('setNotices', sortedResultData.filter(item => item.isPublic == true));
        } else {
          console.warn(result.resultMessage);
        }
      } catch (error) {
        console.log(error);
      }
    },
    saveSelectedZone: async (store, payload) => {
      try {
        const result = await Axios.post(ZoneApi.SELECT_ZONE.replace(':id', payload.id), {}, '', { hideLoading: payload?.hideLoading });
        console.log('saveSelectedZone => result : ', result);
      } catch (error) {
        console.log(error);
      }
    },

    // payload : {controllerId : , type : , item : {}}
    // 현재 조명에 대한 처리만 가능함, 아직 커튼 및 보일러등에 대한 정의는 없음.
    changeDeviceState: async (store, payload) => {
      console.log('home/changeDeviceState => payload : ', payload);

      var ports = payload.data.port != undefined 
        ? (Array.isArray(payload.data.port) ? payload.data.port : [payload.data.port]) 
        : payload.data.ports;

      // const arrayEquals = (a,b) =>{
      //   return Array.isArray(a) &&
      //   Array.isArray(b) &&
      //   a.length === b.length &&
      //   a.every((val, index) => val === b[index])
      // }

      // var device = store.state.zones
      //   .find((z) =>
      //     z.groups.some((g) =>
      //       g.devices.some((d) => d.controllerId === payload.serial),
      //     ),
      //   )
      //   .groups.find((g) =>
      //     g.devices.some(
      //       (d) =>
      //         d.controllerId === payload.serial &&
      //         d.type === payload.type &&
      //         ((d.type === DeviceType.Lamp && d.item.ports.includes(ports)) ||
      //           (d.type > DeviceType.Lamp &&
      //             d.item.serial === payload.data.serial)),
      //     ),
      //   )
      //   .devices.find(
      //     (d) =>
      //       d.controllerId === payload.serial &&
      //       d.type === payload.type &&
      //       ((d.type === DeviceType.Lamp && d.item.ports.includes(ports)) ||
      //         (d.type > DeviceType.Lamp &&
      //           d.item.serial === payload.data.serial)),
      //   );

      const devices = [];
      store.state.zones.find((z) =>
        z.groups.some((g) =>
          g.devices.some((d) => d.controllerId === payload.serial),
        )
      )?.groups.filter((g) =>
        g.devices.some(
          (d) =>
            d.controllerId === payload.serial &&
            d.type == payload.type &&
            ((d.type == DeviceType.Lamp && d.item?.ports?.some(port => ports.includes(port))) 
            || (d.type > DeviceType.Lamp &&
                d.item?.serial === payload.data.serial)),
        )
      )?.forEach((g) => {
        g.devices.forEach((d) => {
          if (d.controllerId === payload.serial && d.type === payload.type && 
            ((d.type == DeviceType.Lamp && d.item?.ports?.some(port => ports.includes(port))) 
            || (d.type > DeviceType.Lamp && d.item?.serial === payload.data.serial))) {
                devices.push(d);
          }
        })
      })

      devices.forEach(device => {
        console.log('call store/home.actions.changeDeviceState() => commit :', device);
      });

      const memberReleases = [];
      const reportOnoff = payload.data.onoff;
      const fromMappingHost = isPhysicalMappingHostOrigin(payload);
      for (const device of devices) {
        const values = {
          onoff: reportOnoff,
          bright: payload.data.bright,
          color: payload.data.color,
        };

        const hostId = await resolveMappingHostId(device._id);
        if (shouldSkipMemberHostEcho(device, hostId, values.onoff, values.bright, values.color, { fromMappingHost })) {
          if (memberShowsHostState(device._id)) {
            store.commit('setDeviceState', {
              deviceId: device._id,
              type: payload.type,
              state: { onoff: values.onoff, bright: values.bright, color: values.color, onVirtual: false },
            });
          } else {
            patchMappingMemberOnVirtual(store, device._id, Number(values.onoff) === 1);
          }
          continue;
        }

        const stateValues = sceneRelease.lampReportState(device, values);
        store.commit('setDeviceState', {
          deviceId: device._id,
          type: payload.type,
          state: stateValues,
        });

        if (!device.isVirtual && await isPhysicalMappingHostDevice(device._id)) {
          await onPhysicalMappingHostControl(
            store,
            device._id,
            values.onoff,
            values.bright,
            values.color,
          );
        }

        if (device.isVirtual) continue;
        const savedLook = sceneRelease.isIndividualSceneTakeover(
          device,
          values.onoff,
          values.bright,
          values.color,
        );
        store.commit('patchDeviceById', {
          deviceId: device._id,
          data: { onVirtual: false },
        });
        memberReleases.push({
          id: device._id,
          savedLook,
        });
      }

      if (payload.type === DeviceType.Lamp && memberReleases.length > 0) {
        for (const rel of memberReleases) {
          await store.dispatch('syncVirtualButtonsOffForMemberDevice', {
            memberDeviceId: rel.id,
            onoff: reportOnoff,
            savedLook: rel.savedLook,
          });
        }
      }
    },

    syncVirtualButtonsOffForMemberDevice: async (store, payload) => {
      const memberDeviceId = payload != null && typeof payload === 'object'
        ? payload.memberDeviceId
        : payload;
      const reportOnoff = payload != null && typeof payload === 'object'
        ? payload.onoff
        : undefined;
      const savedLook = payload != null && typeof payload === 'object' && payload.savedLook === true;
      try {
        if (!savedLook && sceneRelease.memberReleaseSuppressed(
          {
            until: store.state.virtualButtonOffSuppressUntil || 0,
            onoff: store.state.virtualButtonOffSuppressOnoff,
          },
          Date.now(),
          reportOnoff,
        )) {
          return;
        }
        const result = await Axios.get(
          VirtualDeviceApi.VIRTUAL_DEVICES_BY_MEMBER.replace(
            ':deviceId',
            String(memberDeviceId),
          ),
          {},
          '',
          { hideLoading: true },
        );
        const list = result?.resultData || [];
        if (!Array.isArray(list) || list.length === 0) return;
        store.commit('setVirtualButtonOffIfLinked', {
          virtualDocs: list,
          reportedMemberId: memberDeviceId,
        });
      } catch (e) {
        console.error('virtualDevices by member 조회 실패:', memberDeviceId, e);
      }
    },

    changeDevicesPower: async (store, payload) => {
      console.log('home/changeDevicesPower => payload : ', payload);

      const ports = payload.data?.ports;
      const onoff = payload.data?.onoff;
      if (!payload.serial || payload.type == null || !ports || !Array.isArray(ports) || ports.length === 0 || onoff === undefined) return;

      const devices = [];
      store.state.zones.find((z) =>
        z.groups.some((g) =>
          g.devices.some((d) => d.controllerId === payload.serial),
        )
      )?.groups.filter((g) =>
        g.devices.some(
          (d) =>
            d.controllerId === payload.serial &&
            d.type == payload.type &&
            d.type == DeviceType.Lamp &&
            d.item?.ports?.some(port => ports.includes(port)),
        )
      )?.forEach((g) => {
        g.devices.forEach((d) => {
          if (d.controllerId === payload.serial && d.type === payload.type &&
            d.type == DeviceType.Lamp && d.item?.ports?.some(port => ports.includes(port))) {
            devices.push(d);
          }
        })
      });

      devices.forEach(device => {
        store.commit('setDeviceState', {
          deviceId: device._id,
          type: payload.type,
          state: { onoff, onVirtual: false }
        });
      });
    },

    changeDevicesState: async (store, payload) => {
      console.log('home/changeDevicesState => payload : ', payload);

      if (payload.cmd !== 'cs') return;

      const ports = payload.data?.ports;
      const onoffArr = payload.data?.onoff;
      const brightArr = payload.data?.bright;
      const colorArr = payload.data?.color;
      const vcArr = payload.data?.vc;
      if (!payload.serial || payload.type == null || !ports || !Array.isArray(ports) || ports.length === 0) return;

      const arrPorts = ports;
      const arrOnoff = Array.isArray(onoffArr) ? onoffArr : (onoffArr !== undefined ? [onoffArr] : []);
      const arrBright = Array.isArray(brightArr) ? brightArr : (brightArr !== undefined ? [brightArr] : []);
      const arrColor = Array.isArray(colorArr) ? colorArr : (colorArr !== undefined ? [colorArr] : []);
      const arrVc = Array.isArray(vcArr) ? vcArr : (vcArr !== undefined ? [vcArr] : []);
      const len = arrPorts.length;
      if (arrOnoff.length !== len || arrBright.length !== len || arrColor.length !== len) return;
      const useVc = arrVc.length === len;
      const fromMappingHost = isPhysicalMappingHostOrigin(payload);

      const zones = store.state.zones;
      for (let i = 0; i < arrPorts.length; i++) {
        const port = arrPorts[i];
        const onoff = arrOnoff[i];
        const bright = arrBright[i];
        const color = arrColor[i];

        let device = null;
        const zone = zones.find((z) =>
          z.groups.some((g) =>
            g.devices.some((d) =>
              d.controllerId === payload.serial &&
              d.type === payload.type &&
              d.type == DeviceType.Lamp &&
              (Array.isArray(d.item?.ports) ? d.item.ports.includes(port) : d.item?.ports === port),
            ),
          )
        );
        if (zone) {
          for (const g of zone.groups) {
            device = g.devices.find((d) =>
              d.controllerId === payload.serial &&
              d.type === payload.type &&
              d.type == DeviceType.Lamp &&
              (Array.isArray(d.item?.ports) ? d.item.ports.includes(port) : d.item?.ports === port),
            );
            if (device) break;
          }
        }

        if (device) {
          const hostId = await resolveMappingHostId(device._id);
          const skipMemberState = shouldSkipMemberHostEcho(
            device,
            hostId,
            onoff,
            bright,
            color,
            { fromMappingHost },
          );
          if (skipMemberState) {
            if (memberShowsHostState(device._id)) {
              store.commit('setDeviceState', {
                deviceId: device._id,
                type: payload.type,
                state: { onoff, bright, color, onVirtual: false },
              });
            } else {
              patchMappingMemberOnVirtual(store, device._id, Number(onoff) === 1);
            }
            continue;
          }

          if (useVc && arrVc[i] == 1) {
            store.commit('setVirtualDeviceState', {
              deviceId: device._id,
              type: payload.type,
              state: { onoff, bright, color }
            });
          } else {
            store.commit('setDeviceState', {
              deviceId: device._id,
              type: payload.type,
              state: { onoff, bright, color, onVirtual: false }
            });
          }
        }
      }
    },

    changeVirtualDeviceState: async (store, payload) => {
      console.log('home/changeVirtualDeviceState => payload : ', payload);

      if (payload.cmd !== 'vc') return;

      const ports = payload.data?.ports;
      const onoffArr = payload.data?.onoff;
      const brightArr = payload.data?.bright;
      const colorArr = payload.data?.color;
      if (!payload.serial || payload.type == null || !ports || !Array.isArray(ports) || ports.length === 0) return;

      const arrPorts = ports;
      const arrOnoff = Array.isArray(onoffArr) ? onoffArr : (onoffArr !== undefined ? [onoffArr] : []);
      const arrBright = Array.isArray(brightArr) ? brightArr : (brightArr !== undefined ? [brightArr] : []);
      const arrColor = Array.isArray(colorArr) ? colorArr : (colorArr !== undefined ? [colorArr] : []);
      const len = arrPorts.length;
      if (arrOnoff.length !== len || arrBright.length !== len || arrColor.length !== len) return;

      const zones = store.state.zones;
      for (let i = 0; i < arrPorts.length; i++) {
        const port = arrPorts[i];
        const onoff = arrOnoff[i];
        const bright = arrBright[i];
        const color = arrColor[i];

        let device = null;
        const zone = zones.find((z) =>
          z.groups.some((g) =>
            g.devices.some((d) =>
              d.controllerId === payload.serial &&
              d.type === payload.type &&
              d.type == DeviceType.Lamp &&
              (Array.isArray(d.item?.ports) ? d.item.ports.includes(port) : d.item?.ports === port),
            ),
          )
        );
        if (zone) {
          for (const g of zone.groups) {
            device = g.devices.find((d) =>
              d.controllerId === payload.serial &&
              d.type === payload.type &&
              d.type == DeviceType.Lamp &&
              (Array.isArray(d.item?.ports) ? d.item.ports.includes(port) : d.item?.ports === port),
            );
            if (device) break;
          }
        }

        if (device) {
          store.commit('setVirtualDeviceState', {
            deviceId: device._id,
            type: payload.type,
            state: { onoff, bright, color }
          });
        }
      }
    },

    changeVirtualSwitchControl: async (store, payload) => {
      console.log('home/changeVirtualSwitchControl => payload : ', payload);
      if (payload.cmd !== 'vsc') return;

      await store.dispatch('fetchAllZones');

      const { serial, data } = payload;
      if (!data || serial == null || data.port == null || data.onoff == null) {
        return;
      }
      const { port, onoff } = data;
      const portNum = Number(port);

      const virtualDevice = (store.state.zones || [])
        .flatMap((z) => z.groups || [])
        .flatMap((g) => g.devices || [])
        .find(
          (d) =>
            d.isVirtual &&
            d.virtualMap &&
            String(d.virtualMap.serial) === String(serial) &&
            Number(d.virtualMap.port) === portNum,
        );

      if (virtualDevice) {
        store.commit('setVirtualDeviceState', {
          deviceId: virtualDevice._id,
          type: payload.type,
          state: { onoff },
        });
      }
    },

    changeVirtualSwitchSetting: async (store, payload) => {
      console.log('home/changeVirtualSwitchSetting => payload : ', payload, store.state.virtualSwitchSettingDevice);
      if (!store.state.virtualSwitchSettingDevice) return;
      try {
        const virtualMap = { serial: payload.serial, port: payload.data.port };
        const result = await Axios.patch(`/devices/${store.state.virtualSwitchSettingDevice._id}/virtualMap`, {
          virtualMap,
        });

        if (result.resultCode === '0000') {
          const deviceId = store.state.virtualSwitchSettingDevice._id;
          store.commit('updateDeviceVirtualMap', { deviceId, virtualMap });
          store.commit('setVirtualSwitchSettingDevice', undefined);
          store.commit('increaseVirtualSwtichSettingCount');
        } else {
          console.error('changeVirtualSwitchSetting patchVirtualMap failed:', result.resultMessage);
        }
      } catch (error) {
        console.error('changeVirtualSwitchSetting patchVirtualMap failed:', error);
      }
    },

    changeVirtualBoundControl: async (store, payload) => {
      if (payload?.cmd !== MQTTCommand.VirtualBoundControl) return;
      const rawId = payload?.data?.deviceId;
      if (rawId == null) return;
      const deviceId = Number(rawId);
      if (!Number.isFinite(deviceId)) return;

      // 커튼 물리버튼 바인딩 결과 (kind=curtain)
      if (payload?.data?.kind === 'curtain') {
        const item = {
          curtainMotion: payload.data.curtainMotion || 'idle',
        };
        if (payload.data.percent != null) {
          item.percent = Number(payload.data.percent);
          item.close = Number(payload.data.percent) >= 100 ? 1 : 0;
        } else if (payload.data.action === 'open') {
          item.close = 0;
        } else if (payload.data.action === 'close') {
          item.close = 1;
        }
        for (const zone of store.state.zones || []) {
          for (const group of zone.groups || []) {
            const d = (group.devices || []).find((x) => Number(x._id) === deviceId);
            if (!d) continue;
            store.commit('updateDevice', {
              groupId: group._id,
              id: deviceId,
              data: { item },
            });
            return;
          }
        }
        return;
      }

      const onoff = payload?.data?.onoff;
      if (onoff == null) return;
      store.commit('suppressVirtualButtonOff', { ms: 3500, onoff });
      store.commit('setVirtualDeviceState', {
        deviceId,
        type: payload.type || DeviceType.Lamp,
        state: { onoff },
      });
    },

    dooyaRegisterRequest: async (store, payload) => {
      if (!store.state.dooya?.isOn) return;
      console.log('home/dooyaRegisterRequest => payload : ', payload);
      store.commit('setDooyaSettingDevice', payload);
    },
    dooyaAddressAck: async (store, payload) => {
      console.log('home/dooyaAddressAck => payload : ', payload);
      store.commit('setDooyaAddressStatus', payload.data.status);
    },
    dooyaDirectionAck: async (store, payload) => {
      if (store.state.dooya?.directionStatus !== 1) return;
      console.log('home/dooyaDirectionAck => payload : ', payload);
      store.commit('setDooyaDirectionStatus', payload.data.code ? 0 : 2);
    },
    ronoRegisterRequest: async (store, payload) => {
      if (!store.state.rono?.isOn) return;
      console.log('home/ronoRegisterRequest => payload : ', payload);
      store.commit('setRonoSettingDevice', payload);
    },
    ronoAddressAck: async (store, payload) => {
      console.log('home/ronoAddressAck => payload : ', payload);
      store.commit('setRonoAddressStatus', payload.data.status);
    },
    ronoDirectionAck: async (store, payload) => {
      if (store.state.rono?.directionStatus !== 1) return;
      console.log('home/ronoDirectionAck => payload : ', payload);
      store.commit('setRonoDirectionStatus', payload.data.code ? 0 : 2);
    },
    chanageTempDeviceState: async (store, payload) => {
      console.log('home/chanageTempDeviceState => payload : ', payload);
      
      let state = undefined;
      let targetType = undefined;
      switch(payload.command) {
        case MQTTTempCommand.TempTargetSet:
          state = { targetTemp: parseFloat(payload.value.TempTargetSet) };
          targetType = DeviceType.Boiler;
          break;
        case MQTTTempCommand.RoomTempReal: {
          state = { currentTemp: parseFloat(payload.value.Temperature) };
          const relayOn = parseBoilerRelayOn(payload.value);
          if (relayOn !== undefined) {
            state.relayOn = relayOn;
          }
          targetType = DeviceType.Boiler;
          break;
        }
        case MQTTTempCommand.PowerRl: {
          const relayOn = parseBoilerRelayOn(payload.value);
          if (relayOn === undefined) {
            return;
          }
          state = { relayOn };
          targetType = DeviceType.Boiler;
          break;
        }
        case MQTTTempCommand.AwayModeSet:
          state = { onoff: parseInt(payload.value.AwayModeSet == 0 ? 1 : 0) };
          targetType = DeviceType.Boiler;
          break;
        case MQTTTempCommand.PatternModeSet:
          state = { pattern: parseInt(payload.value.PatternModeSet) };
          targetType = DeviceType.Boiler;
          break;
        case MQTTTempCommand.PatternMode2xSet:
          state = { pattern2x: parseInt(payload.value.PatternMode2XSet) };
          targetType = DeviceType.Boiler;
          break;
        case MQTTTempCommand.TimeOnCycleSet:
          state = { customPattern: { on: payload.value.TimeOnCycleSet1 } };
          targetType = DeviceType.Boiler;
          break;
        case MQTTTempCommand.TimeOffCycleSet:
          state = { customPattern: { off: payload.value.TimeOffCycleSet } };
          targetType = DeviceType.Boiler;
          break;
        case MQTTTempCommand.AirOnCycleSet:
          state = {
            customPattern: {
              on:
                payload.value.AirOnCycleSet ??
                payload.value.AIRONCYCLESET ??
                payload.value.AirOnCycleSet1,
            },
          };
          targetType = DeviceType.Boiler;
          break;
        case MQTTTempCommand.AirOffCycleSet:
          state = {
            customPattern: {
              off:
                payload.value.AirOffCycleSet ??
                payload.value.AIROFFCYCLESET ??
                payload.value.AirOffCycleSet1,
            },
          };
          targetType = DeviceType.Boiler;
          break;
        case MQTTTempCommand.AirconSetPoint: {
          const src =
            payload.value?.ISSETPOINT && typeof payload.value.ISSETPOINT === 'object'
              ? payload.value.ISSETPOINT
              : payload.value || {};
          const aircon = {};
          if (src.airconID != null) aircon.indoorAddress = String(src.airconID);
          if (src.airconProduct != null) aircon.product = src.airconProduct;
          if (src.airconCassetteCount != null) {
            aircon.cassetteCount = Number(src.airconCassetteCount);
          }
          if (src.airconGWAddress != null) {
            aircon.gwAddress = Number(src.airconGWAddress);
          }
          if (src.airconWindSpeed != null) {
            aircon.windSpeed = Number(src.airconWindSpeed);
          }
          if (src.airconOper != null) {
            aircon.operMode = Number(src.airconOper);
          }
          state = { aircon };
          targetType = DeviceType.Boiler;
          break;
        }
        case MQTTTempCommand.DarkModeSet: {
          const src =
            payload.value?.DARKMODESET && typeof payload.value.DARKMODESET === 'object'
              ? payload.value.DARKMODESET
              : payload.value || {};
          const next = {};
          if (src.darkMode != null) next.darkMode = Number(src.darkMode);
          if (src.darkModeStart != null) next.darkModeStart = String(src.darkModeStart);
          if (src.darkModeEnd != null) next.darkModeEnd = String(src.darkModeEnd);
          if (Object.keys(next).length > 0) {
            state = next;
          }
          targetType = DeviceType.Boiler;
          break;
        }

        // case MQTTCurtainCommand.IsScanCount:
        //   state = { customPattern: { off: payload.value.ISScanCount.Data } };
        //   break;

        // case MQTTCurtainCommand.IsOpen:
        //   if (payload.value.ISOpen == "Error") {
        //     return;
        //   }
        //   console.log(payload.value)
        //   state = { close: 0 };
        //   targetType = DeviceType.Curtain;
        //   break;
        // case MQTTCurtainCommand.IsClose:
        //   if (payload.value.ISClose == "Error") {
        //     return;
        //   }
        //   console.log(payload.value)
        //   state = { close: 1 };
        //   targetType = DeviceType.Curtain;
        //   break;
        // case MQTTCurtainCommand.IsStop:
        //   if (payload.value.ISStop == "Error") {
        //     return;
        //   }
        //   console.log(payload.value)
        //   // state = { close: 0 };
        //   targetType = DeviceType.Curtain;
        //   break;
        // case MQTTCurtainCommand.IsSetClosePercent:
        //   if (payload.value.ISSetClosePercent == "Error") {
        //     return;
        //   }
        //   console.log(payload.value)
        //   state = { percent: payload.value.ISSetClosePercent };
        //   targetType = DeviceType.Curtain;
        //   break;
        // case MQTTCurtainCommand.IsReadPercent:
        //   break;
        // case MQTTCurtainCommand.RS485AddrWrite:
        //   if (payload.value.RS485AddrWrite == "Error") {
        //     return;
        //   }
        //   console.log(payload.value)
        //   state = { address: payload.value.RS485AddrWrite };
        //   targetType = DeviceType.Curtain;
        //   break;
        // case MQTTCurtainCommand.RS485Addr:
        //   break;
        // case MQTTCurtainCommand.IsWriteAddress:
        //   break;
        // case MQTTCurtainCommand.IsWriteDirection:
        //   if (payload.value.ISWriteDirection == "Error") {
        //     return;
        //   }
        //   console.log(payload.value)
        //   state = { reverse: payload.value };
        //   targetType = DeviceType.Curtain;
        //   break;
      }

      if (!state) {
        return;
      }

      const devices = [];
      store.state.zones.find((z) =>
        z.groups.some((g) =>
          g.devices.some((d) => d.controllerId === payload.controllerId),
        )
      )?.groups?.filter((g) =>
        g.devices.some(
          (d) => d.controllerId === payload.controllerId && d.type == targetType
        )
      )?.forEach((g) => {
        g.devices.forEach((d) => {
          if (d.controllerId === payload.controllerId && d.type === targetType) {
            devices.push(d);
          }
        })
      });

      if (devices.length === 0) {
        return;
      }

      console.log(state);
      devices.forEach(device => {
        store.commit('setDeviceState', {
          deviceId: device._id,
          type: device.type,
          state
        });
      });
    },

    updateDeviceState: async (store, payload) => {
      // console.log('call store/home.actions.updateDeviceState() => payload : ',payload)
      store.commit('setDeviceState', {
        deviceId: payload.deviceId,
        type: payload.type,
        state: payload.data,
      });
    },

    updateGroupDeviceState: async (store, payload) => {
      // console.log('call store/home.actions.updateGroupDeviceState() => payload : ',payload)
      store.commit('setDeviceState', {
        deviceId: payload.deviceId,
        type: payload.type,
        state: payload.data,
      });
    },
    updateGroupDevicesState: async (store, payload) => {
      if (payload?.cmd === 'vc') {
        store.commit('setVirtualDevicesState', {
          deviceIds: payload.deviceIds,
          type: payload.type,
          state: payload.data,
        });
      } else if (payload?.cmd === 'cs') {
        await store.dispatch('changeDevicesState', payload);
      } else {
        store.commit('setDevicesState', {
          deviceIds: payload.deviceIds,
          type: payload.type,
          state: payload.data,
        });
      }
    },
    changeDeviceSettingMode: async (store, payload) => {
      store.commit('changeDeviceSettingMode', payload);

      // 가상버튼 ↔ 물리 벽버튼 할당: 설정 모드에서 포트 수신 시 저장
      const settingDevice = store.state.boundButtonSettingDevice;
      if (
        !settingDevice ||
        payload?.data?.setting !== SettingMode.InSettingMode ||
        payload?.type !== DeviceType.Lamp ||
        payload?.data?.port === undefined ||
        !payload?.serial
      ) {
        return;
      }

      const failBoundButtonAssign = (errorPayload) => {
        const serial = errorPayload?.serial || payload.serial;
        const port = errorPayload?.port ?? Number(payload.data.port);
        store.commit('setBoundButtonLastError', {
          message: errorPayload?.message || '물리 버튼 할당에 실패했습니다.',
          hint: errorPayload?.hint || '',
          serial,
          port,
        });
        store.commit('setBoundButtonSettingDevice', undefined);
        store.commit('removeSettingModeDevice', {
          serial,
          type: payload.type,
          data: { port },
        });
        store.commit('increaseBoundButtonSettingCount');
      };

      const assignKey = String(settingDevice._id);
      if (boundButtonAssignInflight.has(assignKey)) return;
      boundButtonAssignInflight.add(assignKey);
      boundButtonAssignNotified.delete(assignKey);

      const finishBoundButtonAssign = async ({ absorb = null, restore = null } = {}) => {
        store.commit('updateDeviceBoundButton', {
          deviceId: settingDevice._id,
          boundButton: {
            controllerId: payload.serial,
            port: Number(payload.data.port),
          },
        });
        store.commit('setBoundButtonLastAbsorb', {
          absorb: absorb || null,
          restore: restore || null,
        });
        store.commit('setBoundButtonLastError', undefined);
        store.commit('setBoundButtonSettingDevice', undefined);
        store.commit('removeSettingModeDevice', {
          serial: payload.serial,
          type: payload.type,
          data: { port: Number(payload.data.port) },
        });
        if (boundButtonAssignNotified.has(assignKey)) return;
        boundButtonAssignNotified.add(assignKey);
        store.commit('increaseBoundButtonSettingCount');
      };

      try {
        // 재할당 금지: 이미 바인딩이 있으면 해제 후 새로 할당하도록 유도
        // 같은 포트로 방금 반영된 중복 이벤트는 성공으로 본다
        const existingBb = settingDevice.boundButton;
        if (
          existingBb?.controllerId != null &&
          existingBb?.port != null
        ) {
          if (
            String(existingBb.controllerId) === String(payload.serial) &&
            Number(existingBb.port) === Number(payload.data.port)
          ) {
            await finishBoundButtonAssign();
            return;
          }
          failBoundButtonAssign({
            message: '이미 물리 버튼이 할당되어 있습니다.',
            hint: '먼저 해제한 뒤 새로 할당해주세요.',
          });
          return;
        }

        const boundButton = {
          controllerId: payload.serial,
          port: Number(payload.data.port),
        };

        // 동일 포트가 다른 가상버튼/커튼에 있으면 API 호출 전에 경고 (가로채기 금지)
        const zones = store.getters['home/getZones'] || [];
        let ownerName = null;
        let ownerKind = '장치';
        for (const zone of zones) {
          for (const group of zone.groups || []) {
            for (const d of group.devices || []) {
              if (!d?.boundButton) continue;
              const isCurtain =
                !d.isVirtual && (d.type === 3 || d.type === 5 || d.type === 6);
              if (!d.isVirtual && !isCurtain) continue;
              if (String(d._id) === String(settingDevice._id)) continue;
              if (String(d.boundButton.controllerId) !== String(boundButton.controllerId)) continue;
              if (Number(d.boundButton.port) !== boundButton.port) continue;
              ownerName = d.name || '다른';
              ownerKind = d.isVirtual ? '가상버튼' : '커튼';
              break;
            }
            if (ownerName) break;
          }
          if (ownerName) break;
        }
        if (ownerName) {
          const portLabel = toDisplayLampPort(boundButton.port, boundButton.controllerId);
          failBoundButtonAssign({
            message: `${portLabel}번 포트가 이미 '${ownerName}' ${ownerKind}에 할당되어 있습니다.`,
            hint: '먼저 해당 장치에서 해제한 뒤 할당해주세요.',
            serial: boundButton.controllerId,
            port: boundButton.port,
          });
          return;
        }

        const result = await Axios.patch(`/devices/${settingDevice._id}/boundButton`, {
          boundButton,
        });
        if (result.resultCode === '0000') {
          await finishBoundButtonAssign({
            absorb: result.resultData?.absorb || null,
            restore: result.resultData?.restore || null,
          });
        } else {
          console.error('patchBoundButton failed:', result.resultMessage);
          const raw = result.resultMessage || '물리 버튼 할당에 실패했습니다.';
          const lines = String(raw).split('\n').filter((l) => l.length > 0);
          let savedBb = null;
          for (const zone of store.state.zones || []) {
            for (const group of zone.groups || []) {
              const found = (group.devices || []).find((d) => String(d._id) === assignKey);
              if (found?.boundButton) savedBb = found.boundButton;
            }
          }
          const duplicateOfThisPort =
            raw.includes('이미 물리 버튼이 할당되어 있습니다') &&
            String(savedBb?.controllerId) === String(boundButton.controllerId) &&
            Number(savedBb?.port) === boundButton.port;
          if (duplicateOfThisPort || boundButtonAssignNotified.has(assignKey)) {
            await finishBoundButtonAssign();
            return;
          }
          failBoundButtonAssign({
            message: lines[0] || raw,
            hint: lines.slice(1).join('\n'),
            serial: boundButton.controllerId,
            port: boundButton.port,
          });
        }
      } catch (error) {
        console.error('patchBoundButton failed:', error);
        failBoundButtonAssign({
          message: '물리 버튼 할당에 실패했습니다.',
        });
      } finally {
        boundButtonAssignInflight.delete(assignKey);
      }
    },
    removeSettingModeDevice: async (store, payload) => {
      store.commit('removeSettingModeDevice', payload);
    },

    changeControllerFirmwareUpdateState: async (store, payload) => {
      store.commit('changeControllerFirmwareUpdateState', payload);
    },

    changeZoneEditMode: async (store, payload) => {
      console.log(
        'home/actions.changeZoneEditMode => payload:',
        JSON.stringify(payload),
      );
      store.commit('setZoneEditMode', payload);
    },

    updateControllerFirmwareVersion: async (store, payload) => {
      store.commit('updateControllerFirmwareVersion', payload);
    },

    updateCheckControllers: async (store, payload) => {
      store.commit('updateCheckControllers', payload);
    },
  },
};
